import { HttpRepository, HttpMethod, HttpError } from "../../core/repository/http_repository";
import { TaskHttpRepository } from "../task/task_http_repository";
import { TaskValidationModel } from "../task/task_model";
import { Result } from "../../core/helper/result";

export interface ImportEntry {
  _id: string;
  filename: string;
  model: TaskValidationModel;
  issues: string[];
  warnings: string[];
  selected: boolean;
  revision: number;
  result: "pending" | "created" | "duplicate" | "error";
  resultError?: string;
  taskId?: string;
}
export interface ImportBatch {
  _id: string;
  name: string;
  count: number;
  createdAt: string;
}
export interface ImportPage {
  batch: ImportBatch;
  summary?: { created: number; duplicates: number; excluded: number };
  entries: ImportEntry[];
  readyIds: string[];
  page: number;
}
export class ImportRepository extends HttpRepository {
  history = () => this._jsonRequest<ImportBatch[]>(HttpMethod.GET, "/task-import");
  preview = (files: File[]) => {
    const form = new FormData();
    files.forEach(file => form.append("file", file));
    return this._request<{ id: string; count: number }>(HttpMethod.POST, "/task-import", form);
  };
  page = (id: string, page: number) => this._jsonRequest<ImportPage>(HttpMethod.GET, `/task-import/${id}?page=${page}`);
  getEntry = (batch: string, id: string) => this._jsonRequest<ImportEntry>(HttpMethod.GET, `/task-import/${batch}/entries/${id}`);
  update = (batch: string, entry: ImportEntry, selected: boolean) => this._jsonRequest<ImportEntry>(HttpMethod.PUT, `/task-import/${batch}/entries/${entry._id}`, { model: entry.model, revision: entry.revision, selected });
  commit = (id: string, ids: string[]) => this._jsonRequest<{ results: { id: string; result: string; error?: string }[] }>(HttpMethod.POST, `/task-import/${id}/commit`, { ids });
}
// The ordinary graphic editor saves into the preview, without creating a task.
export class ImportEditorRepository extends TaskHttpRepository {
  private imports = new ImportRepository();
  private revision = 0;
  constructor(private batch: string, private entry: string) { super(); }
  getOne = async (_id: string): Promise<Result<HttpError, TaskValidationModel>> => {
    const result = await this.imports.getEntry(this.batch, this.entry);
    if (result.isFailure()) return Result.error(result.error);
    const record = result.getOrThrow();
    this.revision = record.revision;
    return Result.ok(Object.assign(new TaskValidationModel(), record.model, { _id: this.entry }));
  };
  save = async (model: TaskValidationModel): Promise<Result<HttpError, { id?: string; _id?: string }>> => {
    const result = await this._jsonRequest<ImportEntry>(HttpMethod.PUT, `/task-import/${this.batch}/entries/${this.entry}`, { model: { ...model, _id: undefined }, revision: this.revision, selected: true });
    if (result.isFailure()) return Result.error(result.error);
    const updated = result.getOrThrow();
    this.revision = updated.revision;
    if (updated.issues.length) return Result.error(new HttpError(updated.issues.join("; "), 400));
    return Result.ok({ id: this.entry });
  };
}
